import Cookies from 'js-cookie';
import jwt_decode from 'jwt-decode';
import lscache from 'lscache';

import { SAML_URL } from '../common/environment';
import { trackError } from '../services/configureAnalytics';
import Service from './App';
import { IdpRequest } from './SAMLDTOs';
import { getToken, removeToken, setToken } from '@/utils/localStorage';
import { error } from '@/utils/logging';

const LOGOUT_URL = '/saml/logout/';

/** Represents a SAML service ajax api. */
export class SAMLService {
  /** The endpoint for the service. */
  private endpoint: string = SAML_URL;
  private token: string | undefined;

  constructor(HttpService) {
    HttpService.addInterceptor(
      (response) => response,
      (error) => {
        const r = error.response || {
          status: undefined,
          data: undefined,
          statusText: undefined,
          config: { url: '' },
        };

        if (r && r.status === 401 && !r.config.url.includes(LOGOUT_URL)) {
          this.logout();
        }

        const data = r && r.data ? `${r.data.arg} ${r.data.devMessage}` : '';
        trackError(
          [
            error.config.method,
            error.config.url,
            data,
            r.status,
            r.statusText,
          ].join(' '),
        );
        return Promise.reject(error);
      },
    );

    this.token = getToken();

    if (this.token) {
      try {
        const decodedInfo = jwt_decode(this.token) as any;
        if (decodedInfo?.attr?.UserEmail?.[0]) {
          const email = decodedInfo.attr.UserEmail[0];
          Service.cache.setSSOUserInfo({
            googleToken: '',
            email,
            name: decodedInfo.attr.UserDisplayName?.[0] || '',
            image: '',
            source: 'saml',
          });
        }
      } catch (e) {
        error('Error parsing token in SAMLService constructor', e);
      }
    }

    // Backward compatibility for cookie token - to remove after 27/04/2025
    const handleCookieToken = () => {
      const cookieToken = Cookies.get('session');
      if (!cookieToken) {
        return;
      }

      if (this.token || !cookieToken.includes('Bearer')) {
        Cookies.remove('session');
        return;
      }

      try {
        // cookieToken is in the format of "Bearer <token>"
        const token = cookieToken.split(' ')[1];
        const decodedInfo = jwt_decode(token);

        this.token = token;
        setToken(token, (decodedInfo as { exp: number }).exp);
        Service.http!.authorization(token);
      } catch (e) {
        error('Error parsing cookie token', e);
      } finally {
        Cookies.remove('session');
      }
    };
    handleCookieToken();

    HttpService.authorization(this.token);
  }

  /** Gets information of identity provider for given domain. */
  getIdentityProvider = (req: IdpRequest): void => {
    const url = `${this.endpoint}/sp/authrequest?url=${req.url}&service=${req.service}`;
    window.location.href = url;
  };

  isLoggedIn = (): boolean => !!this.token;

  /** Saves JWT token in localStorage */
  login = (jwtToken: string, expiredAt: number): void => {
    this.token = jwtToken;
    setToken(jwtToken, expiredAt);
    Service.http!.authorization(jwtToken);
  };

  /** Removes JWT token from localStorage and logs out in SAML gateway */
  logout = async () => {
    const url = `${this.endpoint}/logout/`;
    try {
      await Service.http!.post(url);
    } catch (e) {
      console.error('error happened on logout', e);
    }

    Service.cache.setSSOUserInfo(null);
    this.token = undefined;
    removeToken();
    sessionStorage.clear();
    lscache.flush();
    this.logoutSubscribers.forEach((fn) => fn());
  };

  logoutSubscribers: Array<() => void> = [];

  subscribeLogout(fn: () => void) {
    this.logoutSubscribers.push(fn);
  }

  unsubscribeLogout(fn: () => void) {
    this.logoutSubscribers.splice(this.logoutSubscribers.indexOf(fn), 1);
  }
}
